import { useEffect, useRef, useState } from "react";
import { Cpu, Users, Phone, WifiOff } from "lucide-react";
import { api } from "@/lib/api";

const REFRESH_MS = 10000;
const PC_ICON_MAX = 20; // visualization cap

function useCounter(target) {
  const [value, setValue] = useState(target ?? 0);
  const rafRef = useRef();
  const fromRef = useRef(target ?? 0);
  const startRef = useRef(0);
  const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (target == null) return;
    if (reduce) {
      setValue(target);
      return;
    }
    cancelAnimationFrame(rafRef.current);
    fromRef.current = value;
    startRef.current = performance.now();
    const dur = 600;
    const tick = (t) => {
      const p = Math.min(1, (t - startRef.current) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(fromRef.current + (target - fromRef.current) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, reduce]);

  return value;
}

export const LivePCStatus = () => {
  const [status, setStatus] = useState({ live: null, available: null, occupied: null });
  const availableAnim = useCounter(status.live ? status.available : null);
  const occupiedAnim = useCounter(status.live ? status.occupied : null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/public/pc-status");
        if (alive) setStatus(data);
      } catch {
        if (alive) setStatus({ live: false, available: null, occupied: null });
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const total = (status.available || 0) + (status.occupied || 0);
  const iconsAvailable = Math.min(status.available || 0, PC_ICON_MAX);
  const iconsOccupied = Math.min(status.occupied || 0, PC_ICON_MAX);

  return (
    <section
      id="live-pcs"
      data-testid="live-pc-section"
      className="relative py-20 md:py-24 bg-black border-t border-zinc-900 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-sky-400" />
          <span className="font-mono text-[11px] tracking-[0.3em] text-sky-400 uppercase">
            Real-time · Live now
          </span>
          {status.live === true && (
            <span data-testid="live-indicator" className="inline-flex items-center gap-1.5 ml-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="font-mono text-[10px] tracking-widest uppercase text-emerald-400">LIVE</span>
            </span>
          )}
        </div>

        <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
          Gaming PCs <span className="text-sky-400">διαθεσιμα</span> τωρα
        </h2>

        {status.live === null ? (
          <div data-testid="live-loading" className="mt-10 border border-zinc-900 p-10 text-center text-zinc-500 font-mono text-xs tracking-widest uppercase">
            LOADING…
          </div>
        ) : status.live ? (
          <>
            <div className="mt-10 grid md:grid-cols-2 gap-4">
              {/* AVAILABLE */}
              <div data-testid="pc-available-card" className="relative border border-emerald-900/60 bg-gradient-to-br from-emerald-950/60 to-black p-8 overflow-hidden">
                <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-emerald-500/20 blur-[80px] rounded-full" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <Cpu className="w-4 h-4 text-emerald-400" />
                    <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-400 uppercase">Ελευθερα</span>
                  </div>
                  <div className="flex items-end gap-3">
                    <span data-testid="pc-available-count" className="font-display text-white text-7xl md:text-8xl leading-none tabular-nums" style={{ textShadow: "0 0 40px rgba(52, 211, 153, 0.6)" }}>
                      {availableAnim}
                    </span>
                    <span className="text-zinc-500 font-mono text-xs pb-3 uppercase tracking-widest">/ {total} PCs</span>
                  </div>
                  <IconRow count={iconsAvailable} color="emerald" testid="pc-available-icons" />
                </div>
              </div>

              {/* OCCUPIED */}
              <div data-testid="pc-occupied-card" className="relative border border-rose-900/60 bg-gradient-to-br from-rose-950/60 to-black p-8 overflow-hidden">
                <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-rose-500/20 blur-[80px] rounded-full" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-rose-400" />
                    <span className="font-mono text-[10px] tracking-[0.3em] text-rose-400 uppercase">Απασχολημενα</span>
                  </div>
                  <div className="flex items-end gap-3">
                    <span data-testid="pc-occupied-count" className="font-display text-white text-7xl md:text-8xl leading-none tabular-nums" style={{ textShadow: "0 0 40px rgba(244, 63, 94, 0.6)" }}>
                      {occupiedAnim}
                    </span>
                    <span className="text-zinc-500 font-mono text-xs pb-3 uppercase tracking-widest">/ {total} PCs</span>
                  </div>
                  <IconRow count={iconsOccupied} color="rose" testid="pc-occupied-icons" />
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-4">
              <a href="tel:2221020988" data-testid="live-phone-cta" className="inline-flex items-center gap-2 text-xs font-mono tabular-nums text-zinc-300 hover:text-sky-400 transition-colors px-3 py-2 border border-zinc-800 hover:border-sky-400/50">
                <Phone className="w-3.5 h-3.5" /> 22210 20988
              </a>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Auto-refresh · 10s</span>
            </div>
          </>
        ) : (
          <div data-testid="live-offline" className="mt-10 border border-zinc-900 bg-zinc-950/40 p-8 md:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <WifiOff className="w-8 h-8 text-zinc-600 shrink-0" />
            <div className="flex-1">
              <div className="text-white text-lg mb-1">Η ζωντανή διαθεσιμότητα είναι προσωρινά μη διαθέσιμη.</div>
              <div className="text-zinc-500 text-sm">Καλέστε μας για άμεση ενημέρωση.</div>
            </div>
            <a href="tel:2221020988" data-testid="live-offline-phone" className="inline-flex items-center gap-2 bg-sky-400 text-black text-xs font-bold uppercase tracking-widest px-4 py-3 hover:bg-sky-300">
              <Phone className="w-3.5 h-3.5" /> 22210 20988
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

function IconRow({ count, color, testid }) {
  const shell = "w-3.5 h-4 border rounded-[2px]";
  const on = color === "emerald"
    ? "border-emerald-400/80 bg-emerald-500/30 shadow-[0_0_8px_rgba(52,211,153,0.6)]"
    : "border-rose-400/80 bg-rose-500/30 shadow-[0_0_8px_rgba(244,63,94,0.6)]";
  return (
    <div data-testid={testid} className="mt-6 flex flex-wrap gap-1.5">
      {Array.from({ length: 20 }).map((_, i) => (
        <span key={i} className={`${shell} ${i < count ? on : "border-zinc-800 bg-transparent"}`} />
      ))}
    </div>
  );
}
